// Projects transport — carved from `src/features/projects/client.ts`, which is now
// a named re-export shim.
//
// History: `git log --follow -M30% -- packages/agents/src/data/api/projects.ts`.
// The default 50% rename threshold just misses the pair — this header dilutes the
// similarity below it — so the plain `--follow` reads as if the module were born
// here. It was not.
//
// Transport comes from the `@heidi/api` configured-client registry — the registry
// hands back the app's real `scribeHttpV2` singleton, same interceptors and same
// retry/refresh state, so request behaviour is byte-identical. `@/utils/scribeHTTP`
// was the ONLY blocker the census reported on this module, and this is that
// blocker's ratified heal: the same shape `api/routines.ts`, `api/inbox.ts` and
// `api/harness.ts` in this package already ship.
//
// Bodies are otherwise identical to the pre-move source, with the transport call
// swapped for `http()`. The structural `ScribeHttp` view keeps axios types out of this
// package's public surface and types exactly the four config fields these 15 calls
// pass and nothing more:
//
//   `params`             — the list read.
//   `headers`            — `uploadAttachment`'s multipart content type.
//   `signal`             — the same call's cancellation, wired to the composer's
//                          abort on remove-before-finish.
//   `onUploadProgress`   — the progress callback the attachment chip renders. Its
//                          argument is typed to the two fields the body reads, so
//                          a widened axios type cannot leak in through it.
//
// The object shape is preserved exactly, including method NAMES: `remove` is not
// `delete`, and `get` is the single-project read rather than a generic verb. Six
// consumer suites mock this surface by MODULE (`jest.mock('@/features/projects/client')`
// and three relative spellings of it), so the shim at the old path is what keeps
// those literals intercepting and a renamed member is a silent runtime failure.
import { getScribeHttpClients } from '@heidi/api/client';

import type {
  CreateProjectRequest,
  ProjectAttachmentDownloadUrlResponse,
  ProjectAttachmentResponse,
  ProjectDetail,
  ProjectFollowUpQuestionsRequest,
  ProjectFollowUpQuestionsResponse,
  ProjectListItem,
  ProjectListQuery,
  ProjectSubmitTurnRequest,
  ProjectSubmitTurnResponse,
  ResolveProjectApprovalRequest,
  ResolveProjectApprovalResponse,
  SubmitProjectFeedbackRequest,
  SubmitProjectFeedbackResponse,
  UpdateProjectRequest,
} from '../../contracts';

/**
 * The slice of the configured axios instance these calls use. Structural on
 * purpose — see the header.
 */
interface ScribeHttpConfig {
  params?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  onUploadProgress?: (event: { loaded: number; total?: number }) => void;
}

interface ScribeHttp {
  get<T>(url: string, config?: ScribeHttpConfig): Promise<{ data: T }>;
  post<T>(url: string, body?: unknown, config?: ScribeHttpConfig): Promise<{ data: T }>;
  patch<T>(url: string, body?: unknown): Promise<{ data: T }>;
  delete<T>(url: string): Promise<{ data: T }>;
}

const http = (): ScribeHttp => getScribeHttpClients().scribeHttpV2;

const BASE = '/projects';

export interface ProjectListPage {
  items: ProjectListItem[];
  /** Pass back as ``cursor`` to fetch the next page; null when exhausted. */
  next_cursor: string | null;
}

export const projectsClient = {
  list: (params: ProjectListQuery = {}) => http().get<ProjectListPage>(BASE, { params }),

  get: (projectId: string) => http().get<ProjectDetail>(`${BASE}/${projectId}`),

  create: (body: CreateProjectRequest) => http().post<ProjectDetail>(BASE, body),

  update: (projectId: string, body: UpdateProjectRequest) =>
    http().patch<ProjectDetail>(`${BASE}/${projectId}`, body),

  remove: (projectId: string) => http().delete<void>(`${BASE}/${projectId}`),

  save: (projectId: string) =>
    http().post<ProjectDetail>(`${BASE}/${projectId}/save`, {}),

  unsave: (projectId: string) =>
    http().post<ProjectDetail>(`${BASE}/${projectId}/unsave`, {}),

  submitTurn: (projectId: string, body: ProjectSubmitTurnRequest) =>
    http().post<ProjectSubmitTurnResponse>(`${BASE}/${projectId}/turns`, body),

  /**
   * Submit a thumbs-up / thumbs-down rating (with optional comment) on
   * an assistant reply. Routed server-side into the shared
   * ``user_feedback`` collection with ``target_type=PROJECT`` — same
   * pipeline as the Evidence chat's ``POST /chat/feedback``.
   */
  submitFeedback: (projectId: string, body: SubmitProjectFeedbackRequest) =>
    http().post<SubmitProjectFeedbackResponse>(`${BASE}/${projectId}/feedback`, body),

  /**
   * Generate LLM follow-up questions for the project's latest reply.
   * The backend derives the conversation context from the durable
   * transcript, so the body only carries optional presentation hints.
   */
  getFollowUpQuestions: (projectId: string, body: ProjectFollowUpQuestionsRequest = {}) =>
    http().post<ProjectFollowUpQuestionsResponse>(
      `${BASE}/${projectId}/follow-up-questions`,
      body
    ),

  /**
   * Approve or deny a pending HITL gate (typically a
   * `request_human_approval` tool call, though any tool with
   * `requires_confirmation=True` flows through the same endpoint).
   *
   * Backend wraps the harness's persist-first resolution sequence
   * (Mongo CAS, then workflow signal) so a verdict survives a workflow
   * restart between the POST and the resume signal.
   */
  resolveApproval: (
    projectId: string,
    approvalId: string,
    body: ResolveProjectApprovalRequest
  ) =>
    http().post<ResolveProjectApprovalResponse>(
      `${BASE}/${projectId}/approvals/${approvalId}:resolve`,
      body
    ),

  /**
   * Pre-upload a single attachment against a project's durable run.
   *
   * The returned ``id`` is included in the next ``submitTurn`` body's
   * ``attachment_ids`` so the harness appends it as an
   * ``AttachmentBlock`` on the user message; CSV/PDF/text are
   * inlined into the LLM call, images become provider image blocks.
   * Mirrors ``uploadChatAttachment`` (same multipart contract) so the
   * Evidence-style FE composer can drive both surfaces with one
   * lifecycle.
   */
  uploadAttachment: ({
    projectId,
    file,
    onProgressUpdate,
    signal,
  }: {
    projectId: string;
    file: File;
    onProgressUpdate?: (progress: number) => void;
    signal?: AbortSignal;
  }) => {
    const formData = new FormData();
    formData.append('file', file);
    return http().post<ProjectAttachmentResponse>(
      `${BASE}/${projectId}/attachments`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgressUpdate) {
            const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgressUpdate(pct);
          }
        },
      }
    );
  },

  deleteAttachment: (projectId: string, attachmentId: string) =>
    http().delete<void>(`${BASE}/${projectId}/attachments/${attachmentId}`),

  /** Re-mint a presigned URL after the original (1h-TTL) expires. */
  getAttachmentDownloadUrl: (projectId: string, attachmentId: string) =>
    http().get<ProjectAttachmentDownloadUrlResponse>(
      `${BASE}/${projectId}/attachments/${attachmentId}/download-url`
    ),
};
